"use client";

import { useEffect, useState, useRef } from "react";
import type { LegalDocumentSection } from "./types";

type UseSectionNavReturn = {
  activeId: string;
  handleNavigate: (id: string) => void;
};

export const useSectionNav = (
  flatSections: LegalDocumentSection[],
): UseSectionNavReturn => {
  const [activeId, setActiveId] = useState(flatSections[0]?.id ?? "");
  const lockedIdRef = useRef<string | null>(null);

  const handleNavigate = (id: string) => {
    const heading = document.querySelector<HTMLElement>(
      `[data-section-heading="${id}"]`,
    );
    if (!heading) return;

    lockedIdRef.current = id;
    window.history.replaceState(null, "", `#${id}`);
    const top = heading.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: "smooth" });
    setActiveId(id);

    window.setTimeout(() => {
      if (lockedIdRef.current === id) {
        lockedIdRef.current = null;
      }
    }, 700);
  };

  useEffect(() => {
    const syncFromHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      setActiveId(hash || flatSections[0]?.id || "");
    };

    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);

    return () => {
      window.removeEventListener("hashchange", syncFromHash);
    };
  }, [flatSections]);

  useEffect(() => {
    let frameId = 0;

    const syncActiveSection = () => {
      if (lockedIdRef.current) return;

      const headerOffset = 120;
      let nextActiveId = flatSections[0]?.id ?? "";

      for (const section of flatSections) {
        const heading = document.querySelector<HTMLElement>(
          `[data-section-heading="${section.id}"]`,
        );
        if (!heading) continue;

        const top = heading.getBoundingClientRect().top;

        if (top < headerOffset) {
          nextActiveId = section.id;
        } else {
          break;
        }
      }

      setActiveId((current) =>
        current === nextActiveId ? current : nextActiveId,
      );
    };

    const handleScroll = () => {
      if (frameId) return;

      frameId = window.requestAnimationFrame(() => {
        syncActiveSection();
        frameId = 0;
      });
    };

    syncActiveSection();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [flatSections]);

  return { activeId, handleNavigate };
};
